'use client';

import appLogger from '@/utils/logger';
import { WORKSPACE_STORAGE_KEY } from '@/constants/workspace';
import { useAuth } from '@/contexts/AuthProvider';
import { useLocalStorage } from '@/hooks';
import {
  useGetActiveWorkspace,
  useGetWorkspaces,
  useSwitchWorkspace,
} from '@/hooks/useWorkspaceQuery';
import {
  IWorkspaceListItem,
  IWorkspaceWithMembership,
  WorkspaceRole,
  WorkspaceUiPersona,
} from '@/types/workspace';
import { handleShowError } from '@/utils';
import { cancelAllRequests } from '@/utils/axios';
import { cancelAllFetchRequests } from '@/utils/fetch-API';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';

interface WorkspaceProviderProps {
  children: ReactNode;
}

interface WorkspaceContextValue {
  // State
  workspaces: IWorkspaceListItem[];
  activeWorkspace: IWorkspaceWithMembership | null;
  isLoading: boolean;
  isCreateModalOpen: boolean;
  isSwitchingWorkspace: boolean;

  // Error states
  isError: boolean;
  error: Error | null;
  /**
   * True only when GET /workspaces/active ANSWERED and named no workspace. A
   * failed request also leaves `activeWorkspace` null, and treating that as
   * "no workspace" sent every user to onboarding on a transient 5xx.
   */
  hasConfirmedNoWorkspace: boolean;

  // Actions
  switchWorkspace: (workspaceId: number) => Promise<void>;
  refetchWorkspaces: () => void;
  refetchActiveWorkspace: () => void;
  setIsCreateModalOpen: (isOpen: boolean) => void;

  // Computed
  hasWorkspaces: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  // True once the workspaces query has actually answered. `isLoading` is false while the
  // query is still disabled (waiting on hydration/session init), so isSuperAdmin reads false
  // there for everyone -- "not known yet", not "not allowed". Anything that denies access
  // rather than merely hiding a control must wait for this.
  isRoleResolved: boolean;
  // Effective per-workspace role for the current user (#2694).
  workspaceRole: WorkspaceRole;
  isViewer: boolean;
  uiPersona: WorkspaceUiPersona;
  // Defaults false until the active workspace resolves. Gate persona-specific
  // rendering on isLoading when a Standard-experience flash would be misleading.
  isSdr: boolean;
  isAe: boolean;
  isEnterprise: boolean;
  // NEW: Enhanced tier checking
  subscriptionTier: string | null;
  isProfessionalOrHigher: boolean;
  hasAccessToTier: (allowedTiers: string[]) => boolean;
  // GH#1280: Product module gating
  hasModule: (module: 'marketing' | 'sales') => boolean;
  isMarketingOnly: boolean;
}

export const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(undefined);


export const WorkspaceProvider: FC<WorkspaceProviderProps> = ({ children }) => {
  const { isAuthenticated, isSessionInitializing } = useAuth();
  const [activeWorkspaceId, setActiveWorkspaceId] = useLocalStorage(WORKSPACE_STORAGE_KEY, null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isSwitchingWorkspace, setIsSwitchingWorkspace] = useState(false);

  // Handle hydration
  useEffect(() => {
    setIsHydrated(true);

    // Clean up invalid workspace ID from localStorage
    const storedWorkspaceId = localStorage.getItem(WORKSPACE_STORAGE_KEY);
    if (storedWorkspaceId === 'null' || storedWorkspaceId === 'undefined') {
      localStorage.removeItem(WORKSPACE_STORAGE_KEY);
    }
  }, []);

  // Only run queries once authenticated, hydrated, AND the session is fully
  // synced. isAuthenticated can flip true (accessToken lands in localStorage)
  // before Supabase finishes syncing the session during SSO/OAuth sign-in —
  // firing these workspace queries in that window gets a 401 the backend
  // then can't yet honor. withAuth already waits on isSessionInitializing for
  // the same reason; this provider sits above withAuth's gate (root layout),
  // so it needs its own check.
  const queriesEnabled = isAuthenticated && isHydrated && !isSessionInitializing;
  
  // Queries - only run when authenticated and hydrated
  const {
    data: workspacesData,
    isLoading: isLoadingWorkspaces,
    isError: isErrorWorkspaces,
    error: workspacesError,
    refetch: refetchWorkspaces,
  } = useGetWorkspaces(queriesEnabled);

  const {
    data: activeWorkspaceData,
    isLoading: isLoadingActiveWorkspace,
    isError: isErrorActiveWorkspace,
    error: activeWorkspaceError,
    refetch: refetchActiveWorkspace,
  } = useGetActiveWorkspace(queriesEnabled);

  // Mutations
  const { mutateAsync: switchWorkspaceMutation } = useSwitchWorkspace();
  const queryClient = useQueryClient();

  // Extract data
  const workspaces = workspacesData?.workspaces || [];
  const activeWorkspace = activeWorkspaceData?.activeWorkspace || null;



  // REMOVED: Automatic localStorage update to fix race condition
  // localStorage is now only updated manually in switchWorkspace function after API success

  // Set initial workspace ID from activeWorkspace data
  useEffect(() => {
    if (activeWorkspace?.vendorid && !activeWorkspaceId) {
      setActiveWorkspaceId(activeWorkspace.vendorid);
    }
  }, [activeWorkspace?.vendorid, activeWorkspaceId, setActiveWorkspaceId]);

  // Clear workspace data when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setActiveWorkspaceId(null);
    }
  }, [isAuthenticated, setActiveWorkspaceId]);

  // Switch workspace function
  const switchWorkspace = useCallback(
    async (workspaceId: number) => {

      // Guard against concurrent switches
      if (isSwitchingWorkspace) {
        return;
      }

      try {
        setIsSwitchingWorkspace(true);

        // Step 1: API call FIRST - ensure backend accepts the switch
        await switchWorkspaceMutation({ workspaceId });

        // Step 2: Update localStorage after confirmed success
        // This ensures the page knows which workspace to use after reload
        setActiveWorkspaceId(workspaceId);
        localStorage.setItem(WORKSPACE_STORAGE_KEY, String(workspaceId));

        // CRITICAL FIX: Set timestamp to prevent axios interceptor from overwriting
        // localStorage during the brief period after reload when backend might
        // still return stale workspace ID in response headers
        localStorage.setItem('workspaceSwitchTimestamp', String(Date.now()));

        // Step 3: Show success toast before reload
        const targetWorkspace = workspaces.find(w => w.vendorid === workspaceId);
        if (targetWorkspace) {
          toast.success(`Switching to ${targetWorkspace.workspace_name}...`);
        }

        // Step 4: Clear React Query cache to prevent stale data on reload
        // This eliminates race conditions where old workspace data is shown
        // during the brief moment after reload before new data loads
        queryClient.clear();

        // Step 5: CRITICAL FIX - Cancel ALL in-flight requests before reload
        // This includes both axios requests AND native fetch requests
        // This prevents race condition where pending requests complete after
        // localStorage is updated but before page reloads, causing stale
        // workspace IDs to overwrite the new workspace ID
        cancelAllRequests(); // Cancel axios requests
        cancelAllFetchRequests(); // Cancel fetch requests (askIOTApiFetch)

        // Small delay to ensure cancellation propagates
        await new Promise(resolve => setTimeout(resolve, 50));

        // Step 6: Hard refresh to ensure clean state
        // This eliminates race conditions from multiple query invalidations
        // and ensures all components re-initialize with the new workspace context
        window.location.reload();

        // Note: Code below won't execute due to reload, but kept for type safety
        // The reload ensures:
        // - All React Query cache is fresh
        // - All contexts re-initialize
        // - No stale data from previous workspace
      } catch (error) {
        // localStorage remains unchanged on failure
        handleShowError(error);
        toast.error('Failed to switch workspace. Please try again.');

        // Ensure UI reflects the current valid state
        await refetchActiveWorkspace();
        throw error;
      } finally {
        setIsSwitchingWorkspace(false);
      }
    },
    [
      isSwitchingWorkspace,
      switchWorkspaceMutation,
      setActiveWorkspaceId,
      workspaces,
      refetchActiveWorkspace,
    ],
  );

  // Workspace sync event listeners for backend/frontend synchronization
  useEffect(() => {
    const handleWorkspaceSyncMismatch = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const { detail } = customEvent;

      try {
        switch (detail.type) {
          case 'sync_to_server':
            // Backend is authoritative, sync frontend state
            if (detail.correctedId && detail.correctedId !== activeWorkspaceId?.toString()) {

              // Update internal state to match server
              setActiveWorkspaceId(parseInt(detail.correctedId));

              // Workspace just changed — every workspace-scoped query is now stale.
              // Invalidate the entire React Query cache so audiences, campaigns,
              // prospects, signals, persona content, etc. refetch against the new
              // workspace. Without this, pages show empty/stale data until the user
              // manually manipulates a filter that triggers a new query key.
              await queryClient.invalidateQueries();
            }
            break;

          case 'validation_needed':
            // Both frontend and backend need validation

            // Force refetch of active workspace to resolve discrepancy
            await refetchActiveWorkspace();

            // Show user notification about sync validation - commented out to reduce noise
            // toast.warning('Workspace state validated - Workspace information has been refreshed');
            break;

          default:
        }
      } catch (error) {
        appLogger.error('Error handling workspace sync event:', { data: error });
        // toast.error('Failed to synchronize workspace state');
      }
    };

    const handleWorkspaceSyncCorrected = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const { detail } = customEvent;

      // Update internal state to match corrected value
      if (detail.correctedId && detail.correctedId !== activeWorkspaceId?.toString()) {
        setActiveWorkspaceId(parseInt(detail.correctedId));

        // Workspace context changed — every workspace-scoped query is now stale.
        await queryClient.invalidateQueries();
        await Promise.all([refetchActiveWorkspace(), refetchWorkspaces()]);
      }
    };

    // Add event listeners
    window.addEventListener(
      'workspace-sync-mismatch',
      handleWorkspaceSyncMismatch as EventListener,
    );
    window.addEventListener(
      'workspace-sync-corrected',
      handleWorkspaceSyncCorrected as EventListener,
    );

    return () => {
      window.removeEventListener(
        'workspace-sync-mismatch',
        handleWorkspaceSyncMismatch as EventListener,
      );
      window.removeEventListener(
        'workspace-sync-corrected',
        handleWorkspaceSyncCorrected as EventListener,
      );
    };
  }, [
    activeWorkspaceId,
    queryClient,
    refetchActiveWorkspace,
    refetchWorkspaces,
    setActiveWorkspaceId,
  ]);

  // Cross-tab workspace consistency: when another tab switches workspace,
  // the WORKSPACE_STORAGE_KEY changes in localStorage. Detect and sync this
  // tab so data doesn't go stale.
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== WORKSPACE_STORAGE_KEY) return;
      if (event.newValue === event.oldValue) return;
      if (!event.newValue) return;

      const newWorkspaceId = parseInt(event.newValue, 10);
      if (Number.isNaN(newWorkspaceId)) return;
      if (newWorkspaceId === activeWorkspaceId) return;

      appLogger.info('Cross-tab workspace switch detected', {
        from: event.oldValue,
        to: event.newValue,
      });

      setActiveWorkspaceId(newWorkspaceId);
      // Invalidate everything so the active tab refetches against the new workspace.
      queryClient.invalidateQueries();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [activeWorkspaceId, queryClient, setActiveWorkspaceId]);

  // Computed values
  const hasWorkspaces = workspaces.length > 0;
  const isAdmin = activeWorkspace?.user_membership?.is_admin || false;
  const isSuperAdmin = workspacesData?.is_super_admin ?? false;
  const isRoleResolved = workspacesData !== undefined;
  const workspaceRole: WorkspaceRole = activeWorkspace?.user_membership?.workspace_role || (isAdmin ? 'admin' : 'member');
  const isViewer = workspaceRole === 'viewer';
  const uiPersona: WorkspaceUiPersona = activeWorkspace?.user_membership?.effective_ui_persona
    || activeWorkspace?.user_membership?.ui_persona
    || 'general';
  const isSdr = uiPersona === 'sdr';
  const isAe = uiPersona === 'ae';
  const isLoading = isLoadingWorkspaces || isLoadingActiveWorkspace;
  const isError = isErrorWorkspaces || isErrorActiveWorkspace;
  const error = workspacesError || activeWorkspaceError;
  // React Query keeps the last successful data through a failed refetch, so
  // `activeWorkspace` already survives a later 5xx; this is the other half —
  // never reading "the request failed" as "the answer was empty".
  const hasConfirmedNoWorkspace = activeWorkspaceData !== undefined
    && !activeWorkspaceData?.activeWorkspace
    && !isErrorActiveWorkspace;

  

  // Context value
  // Check if workspace is enterprise (legacy)
  const isEnterprise = useMemo(() => {
    return activeWorkspace?.subscriptionplan === 'enterprise';
  }, [activeWorkspace?.subscriptionplan]);

  // NEW: Enhanced tier checking
  const subscriptionTier = activeWorkspace?.subscriptionplan || null;
  
  const isProfessionalOrHigher = useMemo(() => {
    const tier = activeWorkspace?.subscriptionplan;
    return tier === 'professional' || tier === 'professional_trial' || tier === 'pro' || tier === 'enterprise';
  }, [activeWorkspace?.subscriptionplan]);

  const hasAccessToTier = useCallback((allowedTiers: string[]) => {
    const currentTier = activeWorkspace?.subscriptionplan;
    
    if (!currentTier || !allowedTiers.length) {
      return false;
    }
    
    // Direct match
    if (allowedTiers.includes(currentTier)) {
      return true;
    }
    
    // Plan alias map — maps vendors.subscriptionplan to every equivalent + LOWER tier
    // name that may appear in allowedPlans, across both the new keys (prospect/outreach/
    // growth) and legacy aliases (starter|kickstart / standard|professional / pro).
    // Rank-correct: a tier accesses its own aliases and all tiers below it, never above.
    // (GH#2076: a lower tier must NOT alias up to a higher tier's gated features.)
    const legacyMappings = {
      // rank 1 — Prospect (legacy starter/kickstart): equivalents only
      'prospect': ['starter', 'kickstart'],
      'starter': ['prospect', 'kickstart'],
      'kickstart': ['prospect', 'starter'],
      // rank 2 — Outreach (legacy standard/professional): equivalents + rank 1
      'outreach': ['standard', 'professional', 'prospect', 'starter', 'kickstart'],
      'standard': ['outreach', 'professional', 'prospect', 'starter', 'kickstart'],
      'professional': ['outreach', 'standard', 'prospect', 'starter', 'kickstart'],
      'professional_trial': ['outreach', 'standard', 'professional', 'prospect', 'starter', 'kickstart'],
      // rank 3 — Growth (legacy pro): equivalents + rank 2 + rank 1
      'growth': ['pro', 'outreach', 'standard', 'professional', 'prospect', 'starter', 'kickstart'],
      'pro': ['growth', 'outreach', 'standard', 'professional', 'prospect', 'starter', 'kickstart'],
      // rank 4 — Enterprise: every lower tier (both naming schemes) so nav gated on any
      // key — new or legacy — always resolves for enterprise vendors.
      'enterprise': ['growth', 'pro', 'outreach', 'standard', 'professional', 'prospect', 'starter', 'kickstart'],
    };
    
    // Check if current tier maps to any allowed tier
    const mappedTiers = legacyMappings[currentTier as keyof typeof legacyMappings] || [];
    const hasMappedAccess = mappedTiers.some(mapped => allowedTiers.includes(mapped));
    
    return hasMappedAccess;
  }, [activeWorkspace?.subscriptionplan]);

  // GH#1280: Product module gating — [] and undefined both mean "both enabled"
  const hasModule = useCallback((module: 'marketing' | 'sales') => {
    const modules = activeWorkspace?.product_modules;
    if (!modules || modules.length === 0) return true; // default: both enabled
    return modules.includes(module);
  }, [activeWorkspace?.product_modules]);

  // Marketing-only: vendor has marketing module but NOT sales.
  // Used to gate SDR config, hot alerts, billing, channels, champions.
  const isMarketingOnly = useMemo(() => {
    const modules = activeWorkspace?.product_modules;
    if (!modules || modules.length === 0) return false; // default both = not marketing-only
    return modules.includes('marketing') && !modules.includes('sales');
  }, [activeWorkspace?.product_modules]);

  const contextValue = useMemo<WorkspaceContextValue>(
    () => ({
      // State
      workspaces,
      activeWorkspace,
      isLoading,
      isCreateModalOpen,
      isSwitchingWorkspace,

      // Error states
      isError,
      error,
      hasConfirmedNoWorkspace,

      // Actions
      switchWorkspace,
      refetchWorkspaces,
      refetchActiveWorkspace,
      setIsCreateModalOpen,

      // Computed
      hasWorkspaces,
      isAdmin,
      isSuperAdmin,
      isRoleResolved,
      workspaceRole,
      isViewer,
      uiPersona,
      isSdr,
      isAe,
      isEnterprise,
      // NEW: Enhanced tier checking
      subscriptionTier,
      isProfessionalOrHigher,
      hasAccessToTier,
      // GH#1280
      hasModule,
      isMarketingOnly,
    }),
    [
      workspaces,
      activeWorkspace,
      isLoading,
      isCreateModalOpen,
      isSwitchingWorkspace,
      isError,
      error,
      hasConfirmedNoWorkspace,
      hasWorkspaces,
      isAdmin,
      isSuperAdmin,
      isRoleResolved,
      workspaceRole,
      isViewer,
      uiPersona,
      isSdr,
      isAe,
      isEnterprise,
      subscriptionTier,
      isProfessionalOrHigher,
      hasAccessToTier,
      hasModule,
      isMarketingOnly,
      switchWorkspace,
      refetchWorkspaces,
      refetchActiveWorkspace,
    ],
  );

  // Don't provide context when not authenticated
  if (!isAuthenticated) {
    return <>{children}</>;
  }

  // Prevent hydration mismatch by not rendering context until client-side hydration
  if (!isHydrated) {
    return <>{children}</>;
  }

  return <WorkspaceContext.Provider value={contextValue}>{children}</WorkspaceContext.Provider>;
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);

  // Allow usage outside of provider (returns undefined)
  // This is useful for components that might render before auth
  return context;
};

// Hook that requires workspace context
export const useWorkspaceContext = () => {
  const context = useContext(WorkspaceContext);

  if (!context) {
    throw new Error('useWorkspaceContext must be used within WorkspaceProvider');
  }

  return context;
};


// Returns whether the current workspace is on a trial plan.
// Trial workspaces have subscriptionplan = 'professional_trial' (set by setTrial()).
// Use this to gate features that require a paid plan (e.g. dialer).
export const useIsTrialWorkspace = (): boolean => {
  const context = useContext(WorkspaceContext);
  return context?.subscriptionTier === 'professional_trial';
};
